import React, { useCallback, useEffect, useState } from "react";
import uiConfig from "../config/uiConfig";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import SearchIcon from "@mui/icons-material/Search";

import DeleteDepartmentDialog from "../components/departments/DeleteDepartmentDialog";
import DepartmentDialog from "../components/departments/DepartmentDialog";
import DepartmentTable from "../components/departments/DepartmentTable";

import {
  createDepartment,
  deleteDepartment,
  getDepartments,
  updateDepartment,
} from "../services/departmentService";

function Departments() {
  const [departments, setDepartments] = useState([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState([]);

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [deleteRelated, setDeleteRelated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const loadDepartments = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getDepartments();
      const filtered = data.filter((department) => (
        department.department_name.toLowerCase().includes(search.toLowerCase())
      ));

      setDepartments(filtered);

      // Keep only valid selected ids after list/search changes.
      setSelectedDepartmentIds((prev) => {
        const validIds = new Set(filtered.map((department) => department.department_id));
        return prev.filter((id) => validIds.has(id));
      });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "Failed to load departments",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  const handleSave = async (data) => {
    if (isSaving) {
      return;
    }

    try {
      setIsSaving(true);

      if (selectedDepartment) {
        await updateDepartment(selectedDepartment.department_id, data);
      } else {
        await createDepartment(data);
      }

      setOpen(false);
      setSelectedDepartment(null);

      await loadDepartments();

      setSnackbar({
        open: true,
        message: "Department saved successfully",
        severity: "success",
      });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "Operation failed",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const closeDeleteDialog = () => {
    setDeleteOpen(false);
    setSelectedDepartment(null);
    setDeleteRelated(false);
  };

  const handleDelete = async () => {
    if (!selectedDepartment || isDeleting) {
      return;
    }

    try {
      setIsDeleting(true);

      await deleteDepartment(selectedDepartment.department_id, deleteRelated);
      closeDeleteDialog();

      await loadDepartments();

      setSnackbar({
        open: true,
        message: deleteRelated
          ? "Department and related records deleted successfully"
          : "Department deleted successfully",
        severity: "success",
      });
    } catch (error) {
      closeDeleteDialog();
      console.error(error);

      const message = error?.response?.data?.detail || "Delete failed";
      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDepartmentIds.length === 0 || isBulkDeleting) {
      return;
    }

    try {
      setIsBulkDeleting(true);

      let successCount = 0;
      let failedCount = 0;

      for (const departmentId of selectedDepartmentIds) {
        try {
          await deleteDepartment(departmentId, true);
          successCount += 1;
        } catch (error) {
          failedCount += 1;
        }
      }

      setBulkDeleteOpen(false);
      await loadDepartments();
      setSelectedDepartmentIds([]);

      if (failedCount === 0) {
        setSnackbar({
          open: true,
          message: `${successCount} department(s) deleted successfully`,
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: `${successCount} deleted, ${failedCount} failed`,
          severity: "warning",
        });
      }
    } catch (error) {
      console.error(error);
      setBulkDeleteOpen(false);
      setSnackbar({
        open: true,
        message: "Bulk delete failed",
        severity: "error",
      });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        background: "#f8fafc",
        minHeight: "100vh",
        p: 3,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={600} color="#0f172a">
            Departments
          </Typography>

          <Typography fontSize={13} color="#64748b">
            Manage and organize all departments in the organization.
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.2}>
          <Button
            variant="outlined"
            color="error"
            startIcon={
              isBulkDeleting
                ? <CircularProgress size={16} color="inherit" />
                : <DeleteSweepIcon />
            }
            onClick={() => setBulkDeleteOpen(true)}
            disabled={selectedDepartmentIds.length === 0 || isBulkDeleting}
            sx={{
              height: 38,
              textTransform: "none",
              borderRadius: 2,
              px: 2,
            }}
          >
            {isBulkDeleting ? "Deleting..." : `Delete All (${selectedDepartmentIds.length})`}
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedDepartment(null);
              setOpen(true);
            }}
            sx={{
              background: uiConfig.buttonColor,
              textTransform: "none",
              borderRadius: 2,
            }}
          >
            Add Department
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: 3,
          p: 1.5,
          mb: 2,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search department name..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#64748b" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <DepartmentTable
        departments={departments}
        loading={loading}
        selectedIds={selectedDepartmentIds}
        onSelectionChange={setSelectedDepartmentIds}
        onEdit={(department) => {
          setSelectedDepartment(department);
          setOpen(true);
        }}
        onDelete={(department) => {
          setSelectedDepartment(department);
          setDeleteRelated(false);
          setDeleteOpen(true);
        }}
      />

      <DepartmentDialog
        open={open}
        department={selectedDepartment}
        isSaving={isSaving}
        onClose={() => {
          if (isSaving) {
            return;
          }
          setOpen(false);
          setSelectedDepartment(null);
        }}
        onSave={handleSave}
      />

      <DeleteDepartmentDialog
        open={deleteOpen}
        isDeleting={isDeleting}
        department={selectedDepartment}
        deleteRelated={deleteRelated}
        onToggleDeleteRelated={setDeleteRelated}
        onClose={() => {
          if (isDeleting) {
            return;
          }
          closeDeleteDialog();
        }}
        onConfirm={handleDelete}
      />

      <Dialog
        open={bulkDeleteOpen}
        onClose={() => {
          if (isBulkDeleting) {
            return;
          }
          setBulkDeleteOpen(false);
        }}
        disableEscapeKeyDown={isBulkDeleting}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Selected Departments</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete all selected departments?
            <br />
            <br />
            Selected: <strong>{selectedDepartmentIds.length}</strong>
            <br />
            This will remove related records for each selected department.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setBulkDeleteOpen(false)}
            disabled={isBulkDeleting}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDelete}
            disabled={isBulkDeleting}
            startIcon={
              isBulkDeleting
                ? <CircularProgress size={16} color="inherit" />
                : <DeleteSweepIcon />
            }
          >
            {isBulkDeleting ? "Deleting..." : "Delete All"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default Departments;
